import type { FC } from 'react';
import { useState } from 'react';
import { Scale } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';
import {
  ArtifactCard,
  ArtifactHeader,
  PrimaryButton,
  ScorePill,
  SecondaryButton,
  VerificationBadge,
  readSupplierHeaders,
} from './procurement-parts.tsx';

const messages = defineMessages({
  kind: {
    id: 'comparison.kind',
    defaultMessage: 'Supplier comparison',
    description: 'Eyebrow label for the side-by-side supplier comparison artifact.',
  },
  choose: {
    id: 'comparison.choose',
    defaultMessage: 'Draft the request',
    description: 'Button that takes the selected suppliers into the procurement request draft.',
  },
  refine: {
    id: 'comparison.refine',
    defaultMessage: 'Refine the filters',
    description: 'Button that loops back to change the matching filters.',
  },
  more: {
    id: 'comparison.more',
    defaultMessage: 'Ask for more suppliers',
    description: 'Button requesting additional supplier matches.',
  },
  include: {
    id: 'comparison.include',
    defaultMessage: 'Include {name} in the request',
    description: 'Accessible label for the per-supplier inclusion checkbox.',
  },
  criterion: {
    id: 'comparison.criterion',
    defaultMessage: 'Criterion',
    description: 'Row header label for the comparison criteria column.',
  },
});

interface ComparisonRow {
  criterion: string;
  values: string[];
}

function readRows(value: unknown): ComparisonRow[] {
  return arr(value)
    .filter(
      (entry): entry is Record<string, unknown> =>
        typeof entry === 'object' && entry !== null && !Array.isArray(entry),
    )
    .map((entry) => ({
      criterion: str(entry.criterion),
      values: arr(entry.values).map(str),
    }))
    .filter((entry) => entry.criterion.length > 0);
}

export const SupplierComparison: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const suppliers = readSupplierHeaders(node.props.suppliers);
  const rows = readRows(node.props.rows);
  const tradeoffNote = optStr(node.props.tradeoffNote);
  const [selected, setSelected] = useState<string[]>(() => suppliers.map((entry) => entry.id));

  const toggle = (id: string) => {
    setSelected((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );
  };

  return (
    <ArtifactCard>
      <ArtifactHeader
        kind={intl.formatMessage(messages.kind)}
        title={str(node.props.title)}
        sourceNote={str(node.props.sourceNote)}
      />

      <div className="overflow-x-auto overflow-y-hidden">
        <table className="w-full min-w-[36rem] border-collapse text-left">
          <caption className="sr-only">{str(node.props.title)}</caption>
          <thead>
            <tr>
              <th scope="col" className="w-40 border-b border-border pb-3 pr-4 align-bottom">
                <span className="text-body-xs font-semibold uppercase tracking-caps text-muted-foreground">
                  {intl.formatMessage(messages.criterion)}
                </span>
              </th>
              {suppliers.map((supplier) => (
                <th
                  key={supplier.id}
                  scope="col"
                  className="min-w-[11rem] border-b border-border px-4 pb-3 align-bottom"
                >
                  <label className="flex cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      checked={selected.includes(supplier.id)}
                      onChange={() => toggle(supplier.id)}
                      aria-label={intl.formatMessage(messages.include, { name: supplier.name })}
                      className="mt-1 h-4 w-4 shrink-0 accent-primary"
                    />
                    <span className="min-w-0">
                      <span
                        className="block text-body-base font-semibold tracking-tight text-foreground"
                        translate="no"
                      >
                        {supplier.name}
                      </span>
                      <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <VerificationBadge
                          tier={supplier.tier}
                          label={supplier.tierLabel}
                          {...(supplier.verifiedOn ? { verifiedOn: supplier.verifiedOn } : {})}
                        />
                        {supplier.score !== undefined ? <ScorePill score={supplier.score} /> : null}
                      </span>
                    </span>
                  </label>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.criterion} className="align-top">
                <th
                  scope="row"
                  className="border-b border-border py-3 pr-4 text-body-sm font-semibold text-muted-foreground"
                >
                  {row.criterion}
                </th>
                {suppliers.map((supplier, index) => (
                  <td
                    key={`${row.criterion}-${supplier.id}`}
                    className="border-b border-border px-4 py-3 text-body-sm text-foreground"
                  >
                    {row.values[index] ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {tradeoffNote ? (
        <p className="flex items-start gap-2 rounded-lg bg-muted p-4 text-body-sm text-muted-foreground">
          <Scale aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-accent" />
          <span className="min-w-0">{tradeoffNote}</span>
        </p>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <PrimaryButton
          label={intl.formatMessage(messages.choose)}
          disabled={selected.length === 0}
          onClick={() => dispatch?.('chooseSuppliers', { selectedIds: selected.join(',') })}
        />
        <SecondaryButton
          label={intl.formatMessage(messages.refine)}
          onClick={() => dispatch?.('refineFilters', {})}
        />
        <SecondaryButton
          label={intl.formatMessage(messages.more)}
          onClick={() => dispatch?.('requestMoreSuppliers', {})}
        />
      </div>
    </ArtifactCard>
  );
};
